import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function PrivacyPolicyPage() {
  usePageMeta('Privacy Policy | OnlineCollegeGuide', 'Learn how OnlineCollegeGuide.io collects, uses, and protects your personal information.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-2103" data-mp="2rraova4fnn1i">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-2104" data-mp="2e178gzs8injz">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-2105" data-mp="2isgvrtqb9esp">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2106" data-mp="245473w57isy3" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-2107" data-mp="290jjskrhaisx">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-2108" data-mp="1v3xqzo6gzk5d">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-2109" data-mp="1z6kmq069hp7u">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-2110" data-mp="2jnq378sokp0o">
          <p data-id="element-2111" data-mp="2es12h24xd22s">
            This privacy notice for OnlineCollegeGuide.io describes how and why
            we might collect, store, use, and/or share your information when you
            use our services, such as when you:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2112" data-mp="1lzxy7sndsjij">
            <li data-id="element-2113" data-mp="20s017lkqzsms">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2114" data-mp="1vy58jsn603bk">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p data-id="element-2115" data-mp="13ccc00sgpkx8">
            <strong className="text-navy" data-id="element-2116" data-mp="13arlqnd6ko56">Questions or concerns?</strong>{' '}
            Reading this privacy notice will help you understand your privacy
            rights and choices. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </div>

        {/* Summary of Key Points */}
        <Section title="Summary of Key Points" data-id="element-2117" data-mp="26r3dwwz9jtdn">
          <SummaryItem label="What personal information do we process?" text="When you visit, use, or navigate our services, we may process personal information depending on how you interact with us, the choices you make, and the features you use." data-id="element-2118" data-mp="26ps2qc3hnjci" />
          <SummaryItem label="Do we process sensitive personal information?" text="We do not process sensitive personal information." data-id="element-2119" data-mp="1j0h7190zwf58" />
          <SummaryItem label="Do we receive information from third parties?" text="We may receive technical and advertising data from third parties, including advertising platforms and analytics providers." data-id="element-2120" data-mp="2ugxqrejl1hxw" />
          <SummaryItem label="How do we process your information?" text="To provide, improve, and administer our services, communicate with you, ensure security and fraud prevention, comply with law, and with your consent where required." data-id="element-2121" data-mp="nr3xjieieytx" />
          <SummaryItem label="When and with whom do we share personal information?" text="Only in specific circumstances (e.g., business transfers or service providers)." data-id="element-2122" data-mp="94aq0zb8exca" />
          <SummaryItem label="How do we keep your information safe?" text="With organizational and technical measures, but no method is 100% secure." data-id="element-2123" data-mp="1t2mhjubynp" />
          <SummaryItem label="What are your rights?" text="Depending on your location, you may have rights under applicable privacy laws." data-id="element-2124" data-mp="3makmp60rq8je" />
          <SummaryItem label="How do you exercise your rights?" text="You can exercise rights by contacting us (see Section 12)." data-id="element-2125" data-mp="1kio81njb0ike" />
        </Section>

        {/* TOC */}
        <Section title="Table of Contents" data-id="element-2126" data-mp="1xigp2u9g8ad6">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2127" data-mp="1xh5kwtc3d0lj">
            {tocItems.map(item => <li key={item.id} data-id="element-2128" data-mp="1iko61s4p13u8">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-2129" data-mp="1nkal87430u5o">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Information Do We Collect?" data-id="element-2130" data-mp="zuyeddw88y6j">
          <SubHeading data-id="element-2131" data-mp="14uuhzc10inlk">Personal information you disclose to us</SubHeading>
          <Paragraph data-id="element-2132" data-mp="qti3l51i2u1s">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our services, when you participate in activities on the services, or
            otherwise when you contact us.
          </Paragraph>
          <Paragraph data-id="element-2133" data-mp="799s69y0wg6m">
            <strong className="text-navy" data-id="element-2134" data-mp="7a2r4oqqq1e7">
              Personal Information Provided by You.
            </strong>{' '}
            The personal information we collect may include:
          </Paragraph>
          <BulletList items={['Names', 'Phone numbers', 'Email addresses', 'Mailing addresses', 'Education level and interests', 'Program preferences']} data-id="element-2135" data-mp="g7lhm8n5l7xh" />

          <SubHeading data-id="element-2136" data-mp="13x7euqjytd2u">Information automatically collected</SubHeading>
          <Paragraph data-id="element-2137" data-mp="zwdrbyafq0ah">
            We automatically collect certain information when you visit, use, or
            navigate the services. This information does not reveal your
            specific identity but may include device and usage information, such
            as:
          </Paragraph>
          <BulletList items={['IP address', 'Browser and device characteristics', 'Operating system', 'Language preferences', 'Referring URLs', 'Device name and type', 'Pages viewed and time spent', 'Clickstream data']} data-id="element-2138" data-mp="6kfll8u3bdxi" />
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="How Do We Process Your Information?" data-id="element-2139" data-mp="p9m0ekrrjle1">
          <Paragraph data-id="element-2140" data-mp="ubuvpns3yzjb">
            We process your personal information for a variety of reasons,
            including:
          </Paragraph>
          <BulletList items={['To facilitate account creation and authentication', 'To deliver and facilitate delivery of services to you', 'To respond to user inquiries and offer support', 'To send administrative information to you', 'To fulfill and manage your requests', 'To enable user-to-user communications', 'To request feedback and contact you about your use of our services', 'To send you marketing and promotional communications', 'To deliver targeted advertising to you', 'To protect our services and investigate potential violations', 'To identify usage trends and determine the effectiveness of campaigns', "To save or protect an individual's vital interest", 'To comply with our legal obligations']} data-id="element-2141" data-mp="13ayfyv30hkze" />

          <SubHeading data-id="element-2142" data-mp="36hdw9canrxqx">Email Communications</SubHeading>
          <Paragraph data-id="element-2143" data-mp="32hn87gcinvq3">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from OnlineCollegeGuide.io and its
            educational partners at the email address you provide. Marketing
            emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-2144" data-mp="3q7855qs3ifqa">
              contact@onlinecollegeguide.io
            </span>
            . Every marketing email includes an unsubscribe link. You may opt
            out at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional or
            administrative messages related to a specific inquiry you have
            submitted.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="SMS / Text Message Communications" data-id="element-2145" data-mp="hq3khb0epuhl">
          <SubHeading data-id="element-2146" data-mp="3zi0fiz7vei4">How We Collect SMS Consent</SubHeading>
          <Paragraph data-id="element-2147" data-mp="8y1mdjdt8qah">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from OnlineCollegeGuide.io and its educational partners at
            the mobile phone number you provide. This consent is obtained at the
            point of form submission and is documented with the date, time, IP
            address, and page URL at which consent was given.
          </Paragraph>

          <SubHeading data-id="element-2148" data-mp="1kipkaox48wk6">How We Use Your Phone Number for SMS</SubHeading>
          <Paragraph data-id="element-2149" data-mp="1fkxuc0g17kim">
            If you have provided consent, we may send you text messages for
            purposes including:
          </Paragraph>
          <BulletList items={['Information about educational programs matching your interests', 'Follow-up communications related to your inquiry', 'Promotional offers and marketing messages from us or our educational partners']} data-id="element-2150" data-mp="rvll50kpfcgb" />

          <SubHeading data-id="element-2151" data-mp="wsvbqjjtxu5q">Consent Is Not Required</SubHeading>
          <Paragraph data-id="element-2152" data-mp="2d7mzuk9y0f98">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services or receiving information. You may choose not to
            provide consent and still access our website and resources.
          </Paragraph>

          <SubHeading data-id="element-2153" data-mp="1bklw9pl9rqrl">Message Frequency and Rates</SubHeading>
          <Paragraph data-id="element-2154" data-mp="1bl3yr3yiz17y">
            Message frequency varies based on your interactions and preferences.
            Standard message and data rates may apply depending on your carrier
            and plan.
          </Paragraph>

          <SubHeading data-id="element-2155" data-mp="xsxm3rukzcd9">How to Opt Out</SubHeading>
          <Paragraph data-id="element-2156" data-mp="xtp39xnfl7k0">
            You may revoke your consent and opt out of SMS communications at any
            time by:
          </Paragraph>
          <BulletList items={['Replying STOP to any text message you receive from us', 'Contacting us directly using the information in Section 13']} data-id="element-2157" data-mp="nurjf80ya27c" />
          <Paragraph data-id="element-2158" data-mp="2i8itg8n6yo3b">
            Opt-out requests will be honored promptly. After opting out, you may
            receive a single confirmation message. You will not receive further
            SMS marketing messages unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-2159" data-mp="2lovmyixjl4s4">For Assistance</SubHeading>
          <Paragraph data-id="element-2160" data-mp="2pr8h0a2xfe92">
            Reply HELP to any text message for support information, or contact
            us directly using the information in Section 13.
          </Paragraph>

          <SubHeading data-id="element-2161" data-mp="2zimph7cuvoec">Third-Party SMS Senders</SubHeading>
          <Paragraph data-id="element-2162" data-mp="2zk7ksz6zvbd9">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </Paragraph>

          <SubHeading data-id="element-2163" data-mp="2gpip7354udar">Record Retention</SubHeading>
          <Paragraph data-id="element-2164" data-mp="3n8gwv9eeokr7">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with our general
            data retention practices described in Section 6.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="When and With Whom Do We Share Your Personal Information?" data-id="element-2165" data-mp="2xiidt5gyb09n">
          <Paragraph data-id="element-2166" data-mp="37hpw872jll3m">
            We may share information in the following situations:
          </Paragraph>
          <ul className="space-y-3 mt-4" data-id="element-2167" data-mp="2jrvkxtxvg4lh">
            {[{
            label: 'Business Transfers',
            text: 'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.'
          }, {
            label: 'Educational Institutions',
            text: 'We share your information with schools and educational partners that match your interests.'
          }, {
            label: 'Service Providers',
            text: 'With third-party vendors, service providers, contractors, or agents who perform services for us.'
          }, {
            label: 'Advertising Partners',
            text: 'With advertising partners to deliver targeted ads.'
          }, {
            label: 'Legal Requirements',
            text: 'When required by law or to protect rights, safety, and property.'
          }].map(item => <li key={item.label} className="flex gap-3 leading-relaxed text-gray-700" data-id="element-2168" data-mp="uc1gihh9h1ic">
                <span className="text-navy mt-2 w-1.5 h-1.5 rounded-full bg-navy flex-shrink-0" data-id="element-2169" data-mp="z808sg5pn3o1" />
                <span data-id="element-2170" data-mp="lcedxscsppnp">
                  <strong className="text-navy" data-id="element-2171" data-mp="pbx0esk9m8sn">{item.label}:</strong>{' '}
                  {item.text}
                </span>
              </li>)}
          </ul>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Do We Use Cookies and Other Tracking Technologies?" data-id="element-2172" data-mp="3a2e6633mf45a">
          <Paragraph data-id="element-2173" data-mp="3qbdd6wkw8upz">
            Yes, we use cookies and similar tracking technologies to access or
            store information. See our Cookie Notice for more details about how
            we use cookies.
          </Paragraph>
        </NumberedSection>

        {/* Section 6 */}
        <NumberedSection id="section-6" number={6} title="How Long Do We Keep Your Information?" data-id="element-2174" data-mp="3bm7fjd1pfck7">
          <Paragraph data-id="element-2175" data-mp="83ntwtdj74yd">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this privacy notice unless otherwise required
            by law. When we have no ongoing legitimate business need to process
            your personal information, we will delete or anonymize it.
          </Paragraph>
        </NumberedSection>

        {/* Section 7 */}
        <NumberedSection id="section-7" number={7} title="How Do We Keep Your Information Safe?" data-id="element-2176" data-mp="2jtgg6toq57qq">
          <Paragraph data-id="element-2177" data-mp="zch00youolf3">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be
            100% secure.
          </Paragraph>
        </NumberedSection>

        {/* Section 8 */}
        <NumberedSection id="section-8" number={8} title="Do We Collect Information From Minors?" data-id="element-2178" data-mp="1i3ro7836yk3a">
          <Paragraph data-id="element-2179" data-mp="1r1tzcggn2t4a">
            We do not knowingly solicit data from or market to children under 18
            years of age. If you become aware that a child has provided us with
            personal information, please contact us. If we learn we have
            collected personal information from a child under 18, we will delete
            that information.
          </Paragraph>
        </NumberedSection>

        {/* Section 9 */}
        <NumberedSection id="section-9" number={9} title="What Are Your Privacy Rights?" data-id="element-2180" data-mp="14yur3hg2qwol">
          <Paragraph data-id="element-2181" data-mp="101kspcapjaom">
            Depending on your location, you may have the following rights:
          </Paragraph>
          <BulletList items={['The right to access your personal information', 'The right to request correction of your personal information', 'The right to request deletion of your personal information', 'The right to opt out of marketing communications', 'The right to opt out of the sale or sharing of personal information (where applicable)']} data-id="element-2182" data-mp="1906dle3nj1sk" />
          <Paragraph data-id="element-2183" data-mp="1sop35wskkh7s">
            To exercise these rights, please contact us using the information in
            Section 13.
          </Paragraph>

          <SubHeading data-id="element-2184" data-mp="21p3q10l6m4de">Opt Out of Marketing Email</SubHeading>
          <Paragraph data-id="element-2185" data-mp="21mr1g4kx4vrd">
            To opt out of marketing email: Click the unsubscribe link in any
            marketing email you receive from us, or contact us at the address in
            Section 13. Requests are processed within 10 business days.
          </Paragraph>

          <SubHeading data-id="element-2186" data-mp="2bbo5x3j8b6pc">
            California Residents — Opt Out of Sale/Sharing
          </SubHeading>
          <Paragraph data-id="element-2187" data-mp="2g9fzs33fp2w6">
            California residents — opt out of sale/sharing of personal
            information: To exercise your right to opt out under CCPA/CPRA,
            contact us at the address in Section 13 with the subject line{' '}
            <strong className="text-navy" data-id="element-2188" data-mp="1sm85shbghg9g">"California Privacy Opt-Out."</strong>{' '}
            We will process your request within 15 business days.
          </Paragraph>
        </NumberedSection>

        {/* Section 10 */}
        <NumberedSection id="section-10" number={10} title="Your California Privacy Rights (CCPA/CPRA)" data-id="element-2189" data-mp="1jm31ig1k6xzi">
          <Paragraph data-id="element-2190" data-mp="1nog1soxje3ow">
            This section applies only to California residents. Under the
            California Consumer Privacy Act (CCPA), as amended by the California
            Privacy Rights Act (CPRA), you have specific rights regarding your
            personal information.
          </Paragraph>

          <SubHeading data-id="element-2191" data-mp="1p4vjplohvnb5">Categories of Personal Information Collected</SubHeading>
          <Paragraph data-id="element-2192" data-mp="1p6y7dz26d4cp">
            In the preceding 12 months, we have collected the following
            categories of personal information:
          </Paragraph>
          <BulletList items={['Identifiers (e.g., name, email address, phone number, IP address)', 'Personal information categories listed in the California Customer Records statute (e.g., contact information)', 'Internet or other similar network activity (e.g., browsing history, interaction with our website)', 'Education and program-interest information (e.g., degree level, area of study)', 'Inferences drawn from other personal information (e.g., preferences and characteristics)']} data-id="element-2193" data-mp="1gbryusbis86h" />

          <SubHeading data-id="element-2194" data-mp="3d1bgt9e4btxx">How We Use and Share Your Information</SubHeading>
          <Paragraph data-id="element-2195" data-mp="3i17mre6dlvg4">
            We use this information to provide our services, match you with
            educational programs, and for marketing purposes. We may share this
            information with the following categories of third parties:
          </Paragraph>
          <BulletList items={['Educational partners (to fulfill your requests for information)', 'Advertising partners (to deliver targeted advertisements)', 'Service providers (to operate our website and services)']} data-id="element-2196" data-mp="2pb7dl34udw7g" />

          <SubHeading data-id="element-2197" data-mp="343rahm1ef7gg">Your CCPA/CPRA Rights</SubHeading>
          <BulletList items={['Right to Know/Access: You can request information about the categories and specific pieces of personal information we have collected about you.', 'Right to Delete: You can request that we delete your personal information, subject to certain exceptions.', 'Right to Correct: You can request that we correct inaccurate personal information we hold about you.', 'Right to Opt-Out: You have the right to opt out of the sale or sharing of your personal information.', 'Right to Limit Use: You have the right to limit the use and disclosure of sensitive personal information (note: we do not generally collect sensitive personal information).', 'Right to Non-Discrimination: We will not discriminate against you for exercising any of your CCPA rights.']} data-id="element-2198" data-mp="26lrcoep8w1qi" />

          <SubHeading data-id="element-2199" data-mp="1fj5vbh5vbv9b">How to Submit a Request</SubHeading>
          <Paragraph data-id="element-2200" data-mp="i2gzc43lnn7u">
            To exercise your Right to Know, Delete, or Correct, please contact
            us using the information in Section 13. To exercise your Right to
            Opt-Out of the sale or sharing of your personal information, please
            visit our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2201" data-mp="2w9zxxjmaosuz">
              Do Not Sell My Info
            </Link>{' '}
            page.
          </Paragraph>
          <Paragraph data-id="element-2202" data-mp="2maqxq90u6i06">
            We will confirm receipt of your request within 10 business days and
            aim to respond within 45 days. If we require more time, we will
            inform you of the reason and extension period in writing. You may
            also use an authorized agent to submit a request on your behalf,
            provided you supply written permission and verify your own identity
            directly with us.
          </Paragraph>
        </NumberedSection>

        {/* Section 11 */}
        <NumberedSection id="section-11" number={11} title="Controls for Do-Not-Track Features" data-id="element-2203" data-mp="1ylpvj3ilk33c">
          <Paragraph data-id="element-2204" data-mp="1tphchf1a3pqw">
            Most web browsers and some mobile operating systems include a
            Do-Not-Track ("DNT") feature or setting you can activate to signal
            your privacy preference. At this stage, no uniform technology
            standard for recognizing and implementing DNT signals has been
            finalized.
          </Paragraph>
        </NumberedSection>

        {/* Section 12 */}
        <NumberedSection id="section-12" number={12} title="Do We Make Updates to This Notice?" data-id="element-2205" data-mp="22pico7vwkv1y">
          <Paragraph data-id="element-2206" data-mp="27olihujpj5az">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws. The updated version will be indicated by an updated
            "Last updated" date at the top of this privacy notice.
          </Paragraph>
        </NumberedSection>

        {/* Section 13 */}
        <NumberedSection id="section-13" number={13} title="How Can You Contact Us About This Notice?" data-id="element-2207" data-mp="2lmry1uksgilu">
          <Paragraph data-id="element-2208" data-mp="f044g7lz0jru">
            If you have questions or comments about this notice, please contact
            us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2209" data-mp="a199666th2qu">
            <p className="font-bold text-navy text-lg" data-id="element-2210" data-mp="op44zvg4fn85">Ironsmith Media</p>
            <p className="text-gray-700 mt-2" data-id="element-2211" data-mp="jr2j72rb99sz">
              1612 UPAS Street, San Diego, CA 92103
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2212" data-mp="3s877dsi7kd6f">
              Email:{' '}
              <a href="mailto:contact@onlinecollegeguide.io" className="text-navy underline hover:text-crimson font-medium" data-id="element-2213" data-mp="3n8kqn9368esv">
                contact@onlinecollegeguide.io
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-14" data-id="element-2214" data-mp="1cichhdhpz6w5">
      <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-6 tracking-tight" data-id="element-2215" data-mp="3i4tnetdmh4vq">
        {title}
      </h2>
      <div className="space-y-4" data-id="element-2216" data-mp="3n2li1v5nj53m">{children}</div>
    </section>;
}
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-2217" data-mp="8jmjp6dxa3">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-2218" data-mp="4x8pati44y0o">
        <span className="text-crimson font-bold text-xl" data-id="element-2219" data-mp="9wbklm8ajpvg">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-2220" data-mp="9uiec81lb7nz">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-2221" data-mp="ipex9sb43yp1">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-2222" data-mp="smjkzwklw27y">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-2223" data-mp="12skr43ddag3t">{children}</p>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-2224" data-mp="ssty664gg95u">
      {items.map(item => <li key={item} className="text-gray-700 leading-relaxed" data-id="element-2225" data-mp="ori8k0lkznxn">
          {item}
        </li>)}
    </ul>;
}
function SummaryItem({
  label,
  text
}: {
  label: string;
  text: string;
}) {
  return <div className="border-l-2 border-crimson/40 pl-4 py-1" data-id="element-2226" data-mp="1giy26elk2axd">
      <p className="text-gray-700 leading-relaxed" data-id="element-2227" data-mp="1gjg4nsyt9ldq">
        <strong className="text-navy block mb-1" data-id="element-2228" data-mp="1brg2ruz6kiei">{label}</strong>
        {text}
      </p>
    </div>;
}
const tocItems = [{
  id: 'section-1',
  label: 'What information do we collect?'
}, {
  id: 'section-2',
  label: 'How do we process your information?'
}, {
  id: 'section-3',
  label: 'SMS / text message communications'
}, {
  id: 'section-4',
  label: 'When and with whom do we share your personal information?'
}, {
  id: 'section-5',
  label: 'Do we use cookies and other tracking technologies?'
}, {
  id: 'section-6',
  label: 'How long do we keep your information?'
}, {
  id: 'section-7',
  label: 'How do we keep your information safe?'
}, {
  id: 'section-8',
  label: 'Do we collect information from minors?'
}, {
  id: 'section-9',
  label: 'What are your privacy rights?'
}, {
  id: 'section-10',
  label: 'Your California Privacy Rights (CCPA/CPRA)'
}, {
  id: 'section-11',
  label: 'Controls for do-not-track features'
}, {
  id: 'section-12',
  label: 'Do we make updates to this notice?'
}, {
  id: 'section-13',
  label: 'How can you contact us about this notice?'
}];